/**
 * Stock Upload API (main API, Dashboard group):
 *   POST /dashboard/stockUpload            multipart { stock_file, store_id } → { upload_id, status, total_rows }
 *   GET  /dashboard/stockUpload/history    ?store_id= → { active: upload | null, history: upload[] }
 *   GET  /dashboard/stockUpload/{id}       → upload (+ error_sample), polled while processing
 *
 * Uploads are processed in the background: a new upload starts as
 * "processing", then the backend marks it "active" (and the previous one
 * "replaced") or "failed" with a fail_reason.
 *
 * Reads return data on success and null on failure (after a toast unless
 * silent), like checklistRequest. Uploads return { ok, ... } so the screen
 * can word the error itself.
 */
import axios from "axios";
import moment from "moment";

import { API_BASE_PATH } from "../config";
import { errorMessage } from "./index";

export const MAX_FILE_SIZE_MB = 20;

export const HISTORY_RANGES = {
  TODAY: "today",
  LAST_7_DAYS: "7d",
  LAST_30_DAYS: "30d",
  ALL: "all",
};

export const UPLOAD_STATUS = {
  PROCESSING: "processing",
  ACTIVE: "active",
  REPLACED: "replaced",
  FAILED: "failed",
};

const BASE = `${API_BASE_PATH}dashboard/stockUpload`;
// A 20 MB file over a slow store connection can take a few minutes.
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

function readJsonKey(key) {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    return null;
  }
}

function authHeaders() {
  return { Authorization: `Bearer ${readJsonKey("token")}` };
}

// → { ok: true, data } or { ok: false, message }. The API also reports
// failures as HTTP 200 with success: false.
async function stockRequest(config, failureMessage) {
  try {
    const res = await axios({ ...config, headers: { ...config.headers, ...authHeaders() } });
    if (res.data?.success) return { ok: true, data: res.data.data ?? null };
    return { ok: false, message: res.data?.msg || failureMessage };
  } catch (error) {
    if (error?.code === "ECONNABORTED") {
      return { ok: false, message: "The upload timed out. Check your connection and try again." };
    }
    return { ok: false, message: error?.response?.data?.msg || failureMessage };
  }
}

async function stockRead(config, failureMessage, { silent = false } = {}) {
  const res = await stockRequest(config, failureMessage);
  if (!res.ok && !silent) errorMessage(res.message);
  return res.ok ? res.data : null;
}

// Anything the backend adds later (queued, pending…) is still in flight.
const normalizeStatus = (raw) => {
  const value = String(raw || "").toLowerCase();
  return Object.values(UPLOAD_STATUS).includes(value) ? value : UPLOAD_STATUS.PROCESSING;
};

/**
 * upload = { id, fileName, uploadedAt, uploadedBy, status, totalRows,
 *            matchedRows, invalidRows, skippedRows, failReason }
 */
export function normalizeUpload(item) {
  if (!item || typeof item !== "object") return null;
  return {
    id: item.id ?? item.upload_id,
    fileName: item.file_name || "—",
    uploadedAt: item.created_at || item.started_at,
    uploadedBy: item.uploaded_by || null,
    status: normalizeStatus(item.status),
    totalRows: item.total_rows,
    matchedRows: item.matched_rows,
    invalidRows: item.invalid_rows,
    skippedRows: item.skipped_rows,
    deactivatedRows: item.deactivated_rows,
    failReason: item.fail_reason || null,
  };
}

/** → { active, history } with history newest first, or null. */
export async function getStockUploadHistory(storeId) {
  const data = await stockRead(
    { method: "get", url: `${BASE}/history`, params: { store_id: String(storeId) } },
    "Couldn't load the stock upload history."
  );
  if (!data || !Array.isArray(data.history)) return null;
  return {
    active: normalizeUpload(data.active),
    history: data.history
      .map(normalizeUpload)
      .filter(Boolean)
      .sort((a, b) => moment(b.uploadedAt).valueOf() - moment(a.uploadedAt).valueOf()),
  };
}

/** A single upload's current state. Silent: polling retries on its own. */
export async function getStockUpload(uploadId) {
  const data = await stockRead(
    { method: "get", url: `${BASE}/${encodeURIComponent(uploadId)}` },
    "Couldn't check the stock upload status.",
    { silent: true }
  );
  return normalizeUpload(data);
}

/**
 * Sends `file` as the store's new stock file.
 * → { ok: true, uploadId, totalRows } or { ok: false, message } (raw backend message).
 */
export async function uploadStockFile(storeId, file, { onProgress } = {}) {
  const form = new FormData();
  form.append("stock_file", file);
  form.append("store_id", String(storeId));
  const res = await stockRequest(
    {
      method: "post",
      url: BASE,
      data: form,
      timeout: UPLOAD_TIMEOUT_MS,
      onUploadProgress: (event) => {
        if (onProgress && event.total) onProgress(Math.round((event.loaded / event.total) * 100));
      },
    },
    "Stock file upload failed. Please try again."
  );
  if (!res.ok) return res;
  return { ok: true, uploadId: res.data?.upload_id, totalRows: res.data?.total_rows };
}

/** The store's display name, or null (e.g. when the store list isn't reachable). */
export async function getStoreName(storeId) {
  const res = await stockRequest(
    { method: "post", url: `${API_BASE_PATH}dashboard/getStores`, data: {} },
    ""
  );
  const stores = res.ok && Array.isArray(res.data) ? res.data : [];
  return stores.find((store) => String(store.store_id) === String(storeId))?.name || null;
}

/** Client-side range filter — the history endpoint has no date params. */
export function filterByRange(uploads, range) {
  const starts = {
    [HISTORY_RANGES.TODAY]: () => moment().startOf("day"),
    [HISTORY_RANGES.LAST_7_DAYS]: () => moment().subtract(6, "days").startOf("day"),
    [HISTORY_RANGES.LAST_30_DAYS]: () => moment().subtract(29, "days").startOf("day"),
  };
  if (!starts[range]) return uploads;
  const start = starts[range]();
  return uploads.filter((upload) => upload.uploadedAt && moment(upload.uploadedAt).isSameOrAfter(start));
}
